import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const OutletSetup = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [outletName, setOutletName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [AutoPrintReceipts, setAutoPrintReceipts] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [GolfOutlets, setGolfOutlets] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateInput = () => {
    if (!outletName) {
      setAlertMessage("Outlet Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    if (
      outletName &&
      !validateString(
        outletName,
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [outletName]);

  useEffect(() => {
    if (fieldValues && fieldValues.OutletName !== null) {
      setOutletName(fieldValues.OutletName);
    }
  }, []);

  const updateGolfOutlets = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        GolfOutlets.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.OUTLETNAME,
        outletName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setGolfOutlets((prev: any) => {
      return prev.map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editGolfOutlets(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${GolfPropertyEnum.outletSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfOutlets = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(GolfOutlets, DNDFieldsEnum.OUTLETNAME, outletName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfOutlets((prev: any) => {
      return updateData(
        prev !== null
          ? [
              ...prev,
              {
                id: GolfOutlets.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfOutlets(null);
    setEditClicked(false);
    setActive(true);
    setAutoPrintReceipts(true);
  };

  const editGolfOutlets = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setOutletName(list?.["Outlet Name"]);
    setActive(list?.Active);
    setAutoPrintReceipts(list?.["Auto Prints Receipts"]);
  };

  const deleteGolfOutlets = (id: number) => {
    setGolfOutlets(deleteItem(GolfOutlets, id));
  };
  useEffect(() => {
    fieldValues && setGolfOutlets(fieldValues);
  }, []);

  useEffect(() => {
    data(GolfOutlets);
    editClicked &&
      setSelectedId(checkData(GolfOutlets, selectedId) ? selectedId : null);
  }, [GolfOutlets]);

  const payload = {
    "Outlet Name": outletName,
    "Auto Prints Receipts": AutoPrintReceipts,
    Active: active,
  };
  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Outlet Setup
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.outletSetupHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfOutlets(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGolfOutlets(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Outlet Name
              <Tooltip title={tooltipComponent(golfTooltipHeaders.outletName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="OutletName"
              variant="outlined"
              placeholder="Outlet Name"
              value={outletName ?? ""}
              helperText={nameError ? validationMessages.outletName : ""}
              error={nameError}
              sx={{ width: "100%" }}
              onChange={(e: any) => {
                setOutletName(e.target.value);
              }}
            />
          </Stack>

          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={AutoPrintReceipts}
                    onChange={() => setAutoPrintReceipts(!AutoPrintReceipts)}
                  />
                }
                label="Auto Print Receipts?"
              />
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.autoPrintReceipts)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateGolfOutlets() : addGolfOutlets()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Outlet
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Outlets Available</Typography>
            <Stack direction="column" gap="10px">
              {GolfOutlets ? (
                <DraggableItemList
                  itemData={GolfOutlets}
                  onOrderUpdate={(order) => setGolfOutlets(order)}
                  onEdit={(info) => editGolfOutlets(info)}
                  onDelete={(id) => deleteGolfOutlets(id)}
                  code={DNDFieldsEnum.OUTLETNAME}
                  name={DNDFieldsEnum.TERMINALID}
                />
              ) : (
                <Typography variant="body2">
                  No Allocation Block Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default OutletSetup;
